import axiosInstance from "@/api/axiosInstance";
import { ENDPOINTS } from "@/constants/api";
import { requestHandler } from "@/helpers/requestHandler";
import { withErrorHandling } from "@/utils/withErrorHandling";

// ---------------------------------------------------------------------------
// Types — mirror the /testdata/* proxy contract (classic backend →
// surfr-dev-simulator, STAGE=TEST only; 404 everywhere else).
// ---------------------------------------------------------------------------

export type SimRunState = "RUNNING" | "STOPPED" | "EXPIRED" | "ERROR" | string;

export interface SimRun {
  id: string;
  scenario: string;
  state: SimRunState;
  liveActors: number;
  startedAt: string | null;
  expiresAt: string | null;
  lastTickAt: string | null;
  tickCount: number;
}

export interface TestDataCaps {
  maxRuns?: number;
  maxLiveActors?: number;
  maxSyntheticStations?: number;
  [key: string]: number | string | undefined;
}

export interface SimPersona {
  name: string;
  display: string;
  /** Skill profile — BEGINNER | INTERMEDIATE | FREERIDER | FREESTYLE | WAVE | FOIL | BIGAIR | PRO */
  skill: string;
  homeSpotId: number;
}

/** An active synthetic wind profile, with its current live value. */
export interface ActiveWindProfile {
  stationId: string;
  name: string;
  lat: number;
  lon: number;
  /** CONSTANT | RAMP | OSCILLATE */
  type: string;
  directionDeg: number;
  currentSpeedKn: number;
  currentGustKn: number;
  createdAt: string;
  expiresAt: string;
}

export interface TestDataStatus {
  target: string;
  scenarios: string[];
  /** Available personas + their skill profiles, for the inject persona selector. */
  personas?: SimPersona[];
  /** Active synthetic wind profiles + their live values (running wind generation). */
  windProfiles?: ActiveWindProfile[];
  caps: TestDataCaps;
  runs: SimRun[];
}

export interface ReadinessCountCheck {
  count: number;
  ok: boolean;
}

export interface ReadinessDetailCheck {
  ok: boolean;
  detail?: string | null;
}

export interface ReadinessOkCheck {
  ok: boolean;
}

export interface TestDataReadiness {
  stations: ReadinessCountCheck;
  forecast: ReadinessDetailCheck;
  liveFriends: ReadinessCountCheck;
  qaJumpToday: ReadinessOkCheck;
}

export interface StartScenarioResponse {
  runId: string;
  scenario?: string;
}

export interface StopAllResponse {
  stopped: number;
}

export type InjectMode = "saved" | "live";

export interface InjectSessionRequest {
  spotId: number;
  /**
   * Geo-translation target for the generated track — required by the simulator
   * (POST /control/inject requires spotId, lat, lon, mode). Pass the selected
   * spot's own coordinates unless deliberately placing the session elsewhere.
   */
  lat: number;
  lon: number;
  persona: string;
  mode: InjectMode;
  /** Only meaningful for mode "live". Minutes (default 90, max 240). */
  durationMinutes?: number;
  /** Kite size in metres (8–12). Omit to auto-pick from the spot's wind. */
  kiteSize?: number;
}

export interface InjectSessionResponse {
  sessionId?: number | string;
  runId?: string;
}

export type WindProfileType = "constant" | "ramp" | "oscillate";

export interface CreateSyntheticWindRequest {
  name: string;
  lat: number;
  lon: number;
  profile: WindProfileType;
  speedKnots: number;
  directionDeg: number;
  /** Profile lifetime in hours, max 48. */
  hours: number;
}

export interface CreateSyntheticWindResponse {
  stationId?: number | string;
  name?: string;
}

export interface RemoveAllSyntheticWindResponse {
  removed: number;
}

// ---------------------------------------------------------------------------
// Service — modeled on WindAlertAdminService (axiosInstance + requestHandler).
// Polled reads (status/readiness) skip the global loading overlay so the
// 10 s auto-refresh doesn't flicker the UI; mutations use the full handler.
// ---------------------------------------------------------------------------

export const TestDataService = {
  async fetchStatus(): Promise<TestDataStatus> {
    return withErrorHandling(async () => {
      const response = await axiosInstance.get(ENDPOINTS.TEST_DATA.STATUS, {
        skipLoading: true,
      });
      return response.data as TestDataStatus;
    }, "TestDataService -> fetchStatus");
  },

  async fetchReadiness(): Promise<TestDataReadiness> {
    return withErrorHandling(async () => {
      const response = await axiosInstance.get(ENDPOINTS.TEST_DATA.READINESS, {
        skipLoading: true,
      });
      return response.data as TestDataReadiness;
    }, "TestDataService -> fetchReadiness");
  },

  async startScenario(scenario: string): Promise<StartScenarioResponse> {
    return requestHandler<StartScenarioResponse>(async () => {
      const response = await axiosInstance.post(ENDPOINTS.TEST_DATA.START, { scenario });
      return response.data as StartScenarioResponse;
    }, "TestDataService -> startScenario");
  },

  async stopAll(): Promise<StopAllResponse> {
    return requestHandler<StopAllResponse>(async () => {
      const response = await axiosInstance.post(ENDPOINTS.TEST_DATA.STOP, {});
      return response.data as StopAllResponse;
    }, "TestDataService -> stopAll");
  },

  async injectSession(req: InjectSessionRequest): Promise<InjectSessionResponse> {
    return requestHandler<InjectSessionResponse>(async () => {
      // Map to the simulator's /control/inject field names (proxied verbatim): it reads
      // `durationMin` and `kiteSize`, not durationMinutes. Omit nulls so the simulator
      // applies its own defaults (duration 90; kite auto-picked from wind).
      const body: Record<string, unknown> = {
        spotId: req.spotId,
        lat: req.lat,
        lon: req.lon,
        personaId: req.persona,
        mode: req.mode,
      };
      if (req.mode === "live" && req.durationMinutes != null) {
        body.durationMin = req.durationMinutes;
      }
      if (req.kiteSize != null) {
        body.kiteSize = req.kiteSize;
      }
      const response = await axiosInstance.post(ENDPOINTS.TEST_DATA.INJECT, body);
      return response.data as InjectSessionResponse;
    }, "TestDataService -> injectSession");
  },

  async stopInjectedRun(id: string): Promise<void> {
    return requestHandler<void>(async () => {
      await axiosInstance.delete(ENDPOINTS.TEST_DATA.INJECT_DELETE(id));
    }, "TestDataService -> stopInjectedRun");
  },

  async createSyntheticWind(
    req: CreateSyntheticWindRequest
  ): Promise<CreateSyntheticWindResponse> {
    return requestHandler<CreateSyntheticWindResponse>(async () => {
      const response = await axiosInstance.post(ENDPOINTS.TEST_DATA.WIND, req);
      return response.data as CreateSyntheticWindResponse;
    }, "TestDataService -> createSyntheticWind");
  },

  async removeAllSyntheticWind(): Promise<RemoveAllSyntheticWindResponse> {
    return requestHandler<RemoveAllSyntheticWindResponse>(async () => {
      const response = await axiosInstance.delete(ENDPOINTS.TEST_DATA.WIND);
      return response.data as RemoveAllSyntheticWindResponse;
    }, "TestDataService -> removeAllSyntheticWind");
  },

  /** Upload a .fit file to be ingested as a QA session. */
  async injectFit(file: File, spotId?: number): Promise<InjectSessionResponse> {
    return requestHandler<InjectSessionResponse>(async () => {
      const form = new FormData();
      form.append("file", file);
      if (spotId !== undefined) form.append("spotId", String(spotId));
      const response = await axiosInstance.post(ENDPOINTS.TEST_DATA.FIT, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return response.data as InjectSessionResponse;
    }, "TestDataService -> injectFit");
  },
};
